import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import moment from 'moment';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { generateUUID } from 'src/app/core/generators/generateUUID';
import { AuthService } from 'src/app/services/AuthService/auth.service';
import {
  ICreateCavaloUpload,
  IGetCavalo
} from 'src/app/services/CavaloService/cavalo.model';
import { CavaloService } from 'src/app/services/CavaloService/cavalo.service';
import { UploadService } from 'src/app/services/UploadService/upload.service';
import Swal from 'sweetalert2';

import { ITabelaCavalo } from './listagem-cavalo-model';
import { SwalAlertService } from 'src/app/services/SwalAlertService/swalAlert.service';

@Component({
  selector: 'app-listagem-cavalo',
  templateUrl: './listagem-cavalo.component.html',
  styleUrls: ['./listagem-cavalo.component.scss']
})
export class ListagemCavaloComponent implements OnInit, OnDestroy {
  isLoading: boolean = false;
  numeroPagina: number = 0;
  private destroy$ = new Subject<void>();

  form = this._inicializarFormulario();
  tableData: ITabelaCavalo[] | Array<any> = [];
  pesquisaParams = {
    TxPesquisar: '',
    DtInicio: '',
    DtFim: '',
    Pagina: 0,
    TamanhoPagina: 20
  };
  maxPages: number = 1;
  expandirCardsMobile: boolean = true;
  guid = generateUUID();
  id: number = Number(localStorage.getItem('id'));

  tableColumns = [
    { header: 'Código', key: 'codigo', type: 'text' },
    { header: 'Placa do Cavalo', key: 'placaCavalo', type: 'text' },
    { header: 'Marca', key: 'Marca', type: 'text' },
    {
      header: 'Suspender',
      key: 'ativo',
      type: 'checkbox',
      action: (item: any) => this.suspenderCavalo(item)
    },
    {
      header: 'Ações',
      key: 'actions',
      type: 'buttons',
      buttons: [
        {
          label: 'Visualizar',
          img: 'assets/eye-icon.png',
          action: (item: any) => this.visualizarCavalo(item)
        },
        {
          label: 'Editar',
          img: 'assets/edit.svg',
          action: (item: any) => this.editarCavalo(item),
          condition: (item: any) => this.permitirEditar(item)
        },
        {
          label: 'Excluir',
          img: 'assets/trashcan.png',
          action: (item: any) => this.excluirCavalo(item)
        }
      ]
    }
  ];
  $event: any;

  constructor(
    private router: Router,
    private fb: FormBuilder,
    private cavaloService: CavaloService,
    private authService: AuthService,
    private uploadService: UploadService,
    private swalAlert: SwalAlertService
  ) {}

  private _inicializarFormulario() {
    return this.fb.group({
      TX_PLCA_CAVA: this.fb.control('', { nonNullable: true }),
      TX_MDLO_CAVA: this.fb.control('', { nonNullable: true })
    });
  }

  ngOnInit() {
    this.getCavalo();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    this.form.valueChanges.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(() => {
      this.getCavalo();
    });
  }

  navegarCavalo() {
    this.router.navigate(['motorista-externo/cadastro/cavalo']);
  }

  mudarPagina(pagina: number) {
    this.pesquisaParams.Pagina = pagina;
    this.numeroPagina = pagina;
    this.getCavalo(pagina + 1);
  }

  formatarCampos(campo: string | number | Date): string {
    return campo === null || campo === '' ? 'Não Informado' : String(campo);
  }

  getHours(date: Date) {
    if (date !== null) {
      return moment(date).format('HH:mm');
    }
    return '******';
  }

  getDate(date: Date) {
    if (date !== null) {
      return moment(date).format('DD/MM/YYYY');
    }
    return '******';
  }

  getCavalo(pageNumber?: number) {
    this.isLoading = true;

    const placa = String(this.form.controls.TX_PLCA_CAVA.value)
      .replace(/-/g, '')
      .trim();
    const marca = String(this.form.controls.TX_MDLO_CAVA.value).trim();

    this.cavaloService
      .GetCavalo({
        placa: placa || undefined,
        marcaCavalo: marca || undefined,
        pageNumber,
        pageSize: 20,
        status: 0
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: IGetCavalo) => {
          this.isLoading = false;
          this.maxPages = response.TOTAL_PAGES;
          this.tableData = response.ITEMS.map(item => {
            return {
              codigo: item.PK_CAVA ?? 0,
              placaCavalo: String(item.TX_PLCA_CAVA),
              Marca: String(item.TX_MDLO_CAVA),
              eixos: item.QT_EIXO_CAVA,
              ano: String(item.NU_ANO_CAVA),
              cor: String(item.TX_COR_CAVA),
              ativo: item.NU_STTS_CAVA == 1 ? false : true,
              situacao: item.NU_STTS_CAVA,
              empresa: item.FK_EPSA,
              externo: 0
            };
          });
        },
        error: (_error: any) => {
          this.isLoading = false;
        }
      });
  }

  editarCavalo(item: any) {
    const queryParams = {
      ...item,
      modoCavalo: 'Editar'
    };
    this.router.navigate(['motorista-externo/cadastro/cavalo'], {
      queryParams: queryParams
    });
  }

  visualizarCavalo(item: any) {
    const queryParams = {
      ...item,
      modoCavalo: 'Visualizar'
    };
    this.router.navigate(['motorista-externo/cadastro/cavalo'], {
      queryParams: queryParams
    });
  }

  excluirCavalo(item: any) {
    this.swalAlert.showAlert(
      'Atenção!',
      'Deseja realizar a exclusão?',
      'warning',
      true,
      true,
      {
        cancelButtonText: 'Cancelar',
        cancelButtonColor: 'red',
        confirmButtonColor: 'green',
        confirmButtonText: 'Confirmar'
      },
      () => {
        this.isLoading = true;
        this.cavaloService.PatchCavaloEmpresa(item.codigo, 0).subscribe(
          response => {
            this.swalAlert.showAlert(
              'Sucesso!',
              'Excluído com sucesso.',
              'warning',
              false,
              false,
              undefined
            );

            this.getCavalo(this.numeroPagina + 1);
          },
          error => {}
        );
        this.isLoading = false;
      }
    );
  }

  suspenderCavalo(item: any) {
    let statusCavalo: number;

    statusCavalo = item.situacao == 1 ? 2 : 1;

    this.cavaloService.PatchCavaloEmpresa(item.codigo, statusCavalo).subscribe(
      response => {
        this.swalAlert.showAlert(
          'Sucesso!',
          `${
            statusCavalo == 1 ? 'Remoção da suspensão' : 'Suspensão'
          } realizada com sucesso.`,
          'warning',
          true,
          false,
          { confirmButtonColor: 'green', confirmButtonText: 'Ok' },
          () => {
            this.getCavalo(this.numeroPagina + 1);
          }
        );
      },
      error => {}
    );
  }

  uploadArquivo(field: any) {
    const base64 = field.split(',')[1];
    this.guid = generateUUID();
    this.isLoading = true;

    const body: ICreateCavaloUpload = {
      ReportBase64: base64,
    };

    if (this.authService.getCurrentUserType() === 'PF') {
      body.FK_MTRT_EXTR = this.id;
    } else {
      body.FK_EPSA = this.id;
    }

    this.uploadService.createByUpload(body, this.guid, 'cavalo').add(() => {
      this.isLoading = false;
      this.getCavalo(this.numeroPagina + 1);
    });
  }

  downloadExcel(): void {
    this.uploadService.downloadTemplateUpload('cavalo');
  }

  permitirEditar(item: any): boolean {
    const status: boolean = item.situacao == 1 ? true : false;
    return status;
  }
}

